/**
 * Pill and StatusDot — the one badge and the one dot (`STYLE.md` §10).
 *
 * Both are the `tokens.css` `.pill` / `.dot` helpers made into components rather than
 * redrawn: 12/16, weight 500, `--r-pill`, the status fills from the canonical file. Features
 * were each growing a `.badgeSomething` of their own, which is how one product ends up with
 * four greens; a feature that needs a badge with a new meaning adds a tone *here*, with the
 * reason, and every screen gets it at once.
 *
 * SIX TONES, AND WHAT EACH ONE IS FOR
 *
 * | Tone | Fill | Means |
 * |---|---|---|
 * | `neutral` | `--neutral-100` | a state with no urgency: draft, archived, closed |
 * | `success` | `--success-50` | done, approved, current |
 * | `warning` | `--warning-50` | needs attention, expiring, superseded |
 * | `danger` | `--danger-50` | rejected, failed, overdue |
 * | `info` | `--neutral-100` | noted — see below |
 * | `rev` | `--neutral-100`, mono | **not a status**: a version number |
 *
 * **`info` is deliberately not blue.** It is the lowest-urgency status, so it gets the lowest
 * contrast; a blue fill beside amber and red would out-shout both and invert the hierarchy.
 * Accent is the focus ring and link marking in this product, never a fill (§3).
 *
 * **`rev` is mono and neutral because a version is an identifier, not a state.** "C" is what
 * a drawing is called this week, not a judgement about it — colouring it would make the real
 * status pill beside it one of two coloured things competing for the same glance.
 *
 * **A pill is never the only signal, and never an ink fill.** `StatusDot` exists for the rows
 * where a full pill is too much furniture; it is 8px, `aria-hidden`, and it always sits beside
 * a text label, because colour alone is not a signal a colour-blind reader receives. It has no
 * `rev` tone for the same reason the pill's `rev` is neutral — there is no dot for a name.
 *
 * Every string is a prop. These components own no catalog keys, including `title`: the full
 * value behind an elided pill is the caller's copy, not this file's.
 */

import styles from "./Pill.module.css";

export type PillTone = "neutral" | "success" | "warning" | "danger" | "info" | "rev";

/** Every tone but `rev`. A version is an identifier and an identifier has no dot. */
export type StatusDotTone = Exclude<PillTone, "rev">;

export interface PillProps {
  readonly tone: PillTone;
  /** The full value, when the label is a shortened form of it. Native `title`, no tooltip. */
  readonly title?: string | undefined;
  readonly children: string;
}

export function Pill({ tone, title, children }: PillProps) {
  return (
    <span className={[styles.pill, styles[tone]].join(" ")} title={title}>
      {children}
    </span>
  );
}

export interface StatusDotProps {
  readonly tone: StatusDotTone;
}

export function StatusDot({ tone }: StatusDotProps) {
  return <span className={[styles.dot, styles[tone]].join(" ")} aria-hidden />;
}
